import type {
  EasyParcelValidationIssue,
  OrderPickupDetails,
  PackingBoxDto,
  PickGroup,
  ShipmentParcelDto,
  ShippingAddressFields,
} from "@gaole/shared";

export type DetailTab = "pick" | "pack" | "ship";

export interface PickListOrderItem {
  id: string;
  name: string;
  quantity: number;
  weightGrams: number;
}

export interface PickListOrderDetail {
  id: string;
  orderNo: string;
  status: string;
  customer: string;
  fulfilmentMethod?: "DELIVERY" | "PICKUP";
  pickupDetails?: OrderPickupDetails | null;
  shippingAddress: ShippingAddressFields;
  addressSummary: string;
  items: PickListOrderItem[];
  parcels: ShipmentParcelDto[];
  suggestedWeightGrams: number;
}

export interface PickListAddressGroup {
  groupKey: string;
  addressSummary: string;
  shippingAddress: ShippingAddressFields;
  orders: PickListOrderDetail[];
}

export interface PickListCustomer {
  userId: string;
  displayName: string;
  email: string;
  addressGroups: PickListAddressGroup[];
}

export interface PickListDetail {
  pickList: {
    id: string;
    code: string;
    status: string;
    createdAt: string;
    completedAt: string | null;
  };
  pickGroups: PickGroup[];
  customers: PickListCustomer[];
  packingBoxes: PackingBoxDto[];
  exportReady: boolean;
  validationIssues: EasyParcelValidationIssue[];
  orders: {
    id: string;
    orderNo: string;
    status: string;
    customer: string;
    trackingNo: string | null;
    courier: string | null;
    fulfilmentMethod?: string;
    pickupDetails?: OrderPickupDetails | null;
    items: { name: string; quantity: number }[];
  }[];
}

export const FIELD_LABEL: Record<string, string> = {
  lengthCm: "length",
  widthCm: "width",
  heightCm: "height",
  weightGrams: "weight",
  parcel: "parcel",
};

export function issueSummary(issue: EasyParcelValidationIssue): string {
  const fields = issue.missingFields.map((f) => FIELD_LABEL[f] ?? f).join(", ");
  const parcel = issue.parcelId ? " (parcel)" : "";
  return `${issue.orderNo}${parcel}: missing ${fields}`;
}

export function parseTrackingLines(text: string): { orderNo: string; trackingNo: string; courier?: string }[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/[,\t|]/).map((p) => p.trim());
      if (parts.length >= 2) {
        return { orderNo: parts[0], trackingNo: parts[1], courier: parts[2] || undefined };
      }
      const space = line.split(/\s+/);
      if (space.length >= 3) {
        return {
          orderNo: space[0],
          trackingNo: space[1],
          courier: space.slice(2).join(" ") || undefined,
        };
      }
      if (space.length >= 2) {
        return { orderNo: space[0], trackingNo: space.slice(1).join(" ") };
      }
      return null;
    })
    .filter((e): e is { orderNo: string; trackingNo: string; courier?: string } => e !== null);
}
